import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Select,
    Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Content from "../components/content";
import Loader from "../components/loader";
import ErrorModal from "../components/error_modal";
import ModalViewMessage from "../components/modal_view_message";
import ModalCreateMessage from "../components/modal_create_message";
import ModalEditMessage from "../components/modal_edit_messsage";
import SwitchCustom from "../components/switch_custom";
import Api from "../libs/api";
import Helper from "../libs/helper";
import Storage from "../libs/storage";
import ModalDeleteMessage from "../components/modal_delete_message";
import Toast from "../components/toast";
import { useNavigate } from "react-router-dom";

const Holder = styled.div`
    display: flex;
    height: 100%;
    width: 100%;
    overflow: auto;
`;

const Filters = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
    flex: 1 1 auto;
    min-width: 0;
`;

const Header = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 30px;
`;

const Data = styled.div`
    margin-bottom: 100px;
`;

const ImageClickable = styled.img`
    cursor: pointer;
`;

const SwitchGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: 10px;

    @media (max-width: 1220px) {
        flex-basis: 100%;
        margin-left: 10;
    }
`;

export default function Messages() {
    const api = Api();
    const helper = Helper();
    const storage = Storage();

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [originalRows, setOriginalRows] = useState([]);

    const [filterAppCliente, setFilterAppCliente] = useState(false);
    const [filterWebAdmin, setFilterWebAdmin] = useState(false);
    const [filterHighlight, setFilterHighlight] = useState(false);
    const [filterRestriction, setFilterRestriction] = useState(false);
    const [filterYear, setFilterYear] = useState(null);
    const [filterMonth, setFilterMonth] = useState(null);

    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState("");

    const [selectedMessage, setSelectedMessage] = useState(null);
    const [modalViewOpen, setModalViewOpen] = useState(false);
    const [modalCreateOpen, setModalCreateOpen] = useState(false);
    const [modalEditOpen, setModalEditOpen] = useState(false);
    const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState("");

    const fetchMessages = async () => {
        try {
            setLoading(true);
            const data = await storage.getItem("data");
            const response = await api.getAllMessages(data.token);
            const messages = response?.data?.allMessages?.edges ?? [];

            const formatted = messages.map(({ node }) => {
                const date = helper.dateFromString(node.createdAt);
                const time = node.createdAt?.split("T")[1]?.slice(0, 5) ?? "-";
                const title = node.title.toUpperCase();
                const highlight = node.highlight ? "SIM" : "NÃO";
                const blockedAreas = node.blockedAreas?.length ? "SIM" : "NÃO";
                const blocksArray = node.blockedAreas || [];
                const companyId = node.companyId;
                const companyName = node.companyName;
                const onlyOwner = node.onlyOwner;
                const platform = [
                    node.platformWeb ? "ADMIN" : "",
                    node.platformApp ? "APP" : "",
                ]
                    .filter(Boolean)
                    .join(", ");
                return {
                    id: parseInt(atob(node.id).split(":")[1]),
                    date,
                    time,
                    title,
                    message: node.message,
                    highlight,
                    blockedAreas,
                    blocksArray,
                    platform,
                    platformApp: node.platformApp,
                    platformWeb: node.platformWeb,
                    companyId,
                    companyName,
                    onlyOwner,
                };
            });
            setRows(formatted);
            setOriginalRows(formatted);
        } catch (error) {
            showErrorModal(
                "Erro ao buscar mensagens. Por favor, tente novamente."
            );
        } finally {
            setLoading(false);
        }
    };

    const showErrorModal = (message) => {
        setErrorModalMessage(message);
        setErrorModalOpen(true);
    };

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const data = await storage.getItem("data");
            const response = await api.allCompanies(data.token);
            return response?.data?.allCompanies?.edges || [];

        } catch (error) {
            showErrorModal(
                "Erro ao buscar empresas. Por favor, tente novamente."
            );
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchMessages();
        fetchCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let filtered = [...originalRows];

        if (filterAppCliente)
            filtered = filtered.filter((r) => r.platform.includes("APP"));
        if (filterWebAdmin)
            filtered = filtered.filter((r) => r.platform.includes("ADMIN"));
        if (filterHighlight)
            filtered = filtered.filter((r) => r.highlight === "SIM");
        if (filterRestriction)
            filtered = filtered.filter((r) => r.blockedAreas === "SIM");
        if (filterYear)
            filtered = filtered.filter((r) =>
                r.date.endsWith(`/${filterYear}`)
            );
        if (filterMonth) {
            const pad = String(filterMonth).padStart(2, "0");
            filtered = filtered.filter((r) => r.date.split("/")[1] === pad);
        }

        setRows(filtered);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filterAppCliente,
        filterWebAdmin,
        filterHighlight,
        filterRestriction,
        filterYear,
        filterMonth,
    ]);

    const mapMessageForEdit = (row) => ({
        id: row.id,
        platformApp: row.platform.includes("APP"),
        platformWeb: row.platform.includes("ADMIN"),
        highlight: row.highlight === "SIM",
        title: row.title,
        message: row.message,
        blockedAreas: row.blockedAreas === "SIM" ? row.blocksArray : [],
        companyId: row.companyId,
        onlyOwner: row.onlyOwner,
    });

    const columns = [
        {
            field: "date",
            headerName: "Data",
            width: 120,
            cellClassName: "date-column",
            headerClassName: "date-header",
        },
        { field: "time", headerName: "Horário", width: 100 },
        { field: "platform", headerName: "Plataforma", width: 120 },
        {
            field: "title",
            headerName: "Título",
            minWidth: 500,
            renderCell: (params) => {
                const text = params.value || "";
                const truncated =
                    text.length > 54 ? text.slice(0, 54) + "..." : text;

                return (
                    <span style={{ color: "#419392", fontWeight: 500 }}>
                        {truncated}
                    </span>
                );
            },
        },
        { field: "highlight", headerName: "Destaque", width: 100 },
        { field: "blockedAreas", headerName: "Bloqueio", width: 100 },
        {
            field: "actions",
            headerName: "Ações",
            sortable: false,
            width: 130,
            renderCell: (params) => (
                <>
                    <Tooltip title="Visualizar">
                        <ImageClickable
                            src="/img/btn_view.svg"
                            alt="Visualizar"
                            onClick={() => {
                                setSelectedMessage(params.row);
                                setModalViewOpen(true);
                            }}
                            style={{ padding: 6 }}
                        />
                    </Tooltip>
                    <Tooltip title="Editar">
                        <ImageClickable
                            src="/img/btn_edit.svg"
                            alt="Editar"
                            onClick={() => {
                                setSelectedMessage(
                                    mapMessageForEdit(params.row)
                                );
                                setModalEditOpen(true);
                            }}
                        />
                    </Tooltip>
                    <Tooltip>
                        <ImageClickable
                            src="/img/btn_trash_red.svg"
                            alt="Excluir"
                            onClick={() => {
                                setSelectedMessage(params.row);
                                setModalDeleteOpen(true);
                            }}
                        />
                    </Tooltip>
                </>
            ),
        },
    ];

    const [showToast, setShowToast] = useState(false);

    const triggerToast = (msg) => {
        setToastMessage(msg);
        setShowToast(true);
    };

    const [canManageMessages, setCanManageMessages] = useState(false);
    useEffect(() => {
        (async () => {
            const data = await storage.getItem("data");
            setCanManageMessages(data?.profile?.permissions?.canManageMessages);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const navigate = useNavigate();

    return (
        <>
            {loading ? (
                <Loader />
            ) : canManageMessages ? (
                <>
                    <Holder>
                        <Content title="Mensagens">
                            <Header>
                                <Filters>
                                    <FormControl
                                        component="fieldset"
                                        size="small"
                                        style={{ marginTop: -10 }}
                                    >
                                        <InputLabel
                                            shrink
                                            sx={{
                                                position: "unset",
                                                transform: "none",
                                                mb: 0,
                                                color: "#909090",
                                                fontSize: 13,
                                            }}
                                        >
                                            PLATAFORMA
                                        </InputLabel>
                                        <FormGroup row>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        sx={{
                                                            p: 0,
                                                            ml: 1,
                                                            mr: 0.5,
                                                        }}
                                                        checked={
                                                            filterAppCliente
                                                        }
                                                        onChange={(e) =>
                                                            setFilterAppCliente(
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                }
                                                label="APP CLIENTE"
                                                sx={{
                                                    "& .MuiFormControlLabel-label":
                                                        {
                                                            fontSize: "14px",
                                                        },
                                                }}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        sx={{
                                                            p: 0,
                                                            ml: 1,
                                                            mr: 0.5,
                                                        }}
                                                        checked={filterWebAdmin}
                                                        onChange={(e) =>
                                                            setFilterWebAdmin(
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                }
                                                label="WEB ADMIN"
                                                sx={{
                                                    "& .MuiFormControlLabel-label":
                                                        {
                                                            fontSize: "14px",
                                                        },
                                                }}
                                            />
                                        </FormGroup>
                                    </FormControl>
                                    <FormControl
                                        size="small"
                                        style={{ width: "100px" }}
                                    >
                                        <InputLabel id="year-label">
                                            Ano
                                        </InputLabel>
                                        <Select
                                            labelId="year-label"
                                            label="Ano"
                                            value={filterYear ?? ""}
                                            onChange={(e) =>
                                                setFilterYear(e.target.value)
                                            }
                                        >
                                            <MenuItem value="">TODOS</MenuItem>
                                            {[2025, 2024, 2023].map((year) => (
                                                <MenuItem
                                                    key={year}
                                                    value={year}
                                                >
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl
                                        size="small"
                                        style={{
                                            width: "100px",
                                            marginRight: 10,
                                        }}
                                    >
                                        <InputLabel id="month-label">
                                            Mês
                                        </InputLabel>
                                        <Select
                                            labelId="year-label"
                                            label="Mês"
                                            value={filterMonth ?? ""}
                                            onChange={(e) =>
                                                setFilterMonth(e.target.value)
                                            }
                                        >
                                            <MenuItem value="">TODOS</MenuItem>
                                            {[
                                                "Janeiro",
                                                "Fevereiro",
                                                "Março",
                                                "Abril",
                                                "Maio",
                                                "Junho",
                                                "Julho",
                                                "Agosto",
                                                "Setembro",
                                                "Outubro",
                                                "Novembro",
                                                "Dezembro",
                                            ].map((mes, index) => (
                                                <MenuItem
                                                    key={mes}
                                                    value={index + 1}
                                                >
                                                    {mes}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <SwitchGroup>
                                        <FormControlLabel
                                            control={
                                                <SwitchCustom
                                                    checked={filterHighlight}
                                                    onChange={(e) =>
                                                        setFilterHighlight(
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label="MENSAGENS COM DESTAQUE"
                                            sx={{
                                                "& .MuiFormControlLabel-label":
                                                    {
                                                        fontSize: "14px",
                                                    },
                                            }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <SwitchCustom
                                                    checked={filterRestriction}
                                                    onChange={(e) =>
                                                        setFilterRestriction(
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label="MENSAGENS COM BLOQUEIO"
                                            sx={{
                                                "& .MuiFormControlLabel-label":
                                                    {
                                                        fontSize: "14px",
                                                    },
                                            }}
                                        />
                                    </SwitchGroup>
                                </Filters>
                                <Button
                                    variant="contained"
                                    onClick={() => setModalCreateOpen(true)}
                                    sx={{
                                        whiteSpace: "nowrap",
                                        flexShrink: 0,
                                        minWidth: 220,
                                        alignSelf: "center",
                                    }}
                                >
                                    + CRIAR NOVA MENSAGEM
                                </Button>
                            </Header>

                            <Data>
                                <DataGrid
                                    rows={rows}
                                    columns={columns}
                                    pageSizeOptions={[10, 25, 50]}
                                    initialState={{
                                        pagination: {
                                            paginationModel: { pageSize: 10 },
                                        },
                                    }}
                                    disableRowSelectionOnClick
                                    sx={{
                                        "& .date-header": {
                                            ml: 2.5,
                                        },
                                        "& .date-column": {
                                            ml: 2.5,
                                        },
                                        "@media print": {
                                            zoom: "75%",
                                        },
                                    }}
                                />
                            </Data>
                        </Content>
                    </Holder>

                    <ErrorModal
                        open={errorModalOpen}
                        onClose={() => setErrorModalOpen(false)}
                        message={errorModalMessage}
                    />
                    <ModalViewMessage
                        open={modalViewOpen}
                        close={() => setModalViewOpen(false)}
                        data={selectedMessage}
                    />
                    <ModalCreateMessage
                        open={modalCreateOpen}
                        close={() => setModalCreateOpen(false)}
                        onSave={async () => {
                            triggerToast("MENSAGEM CRIADA COM SUCESSO!");
                            await fetchMessages();
                        }}
                        showErrorModal={showErrorModal}
                    />
                    {modalEditOpen && selectedMessage && (
                        <ModalEditMessage
                            open={modalEditOpen}
                            close={() => setModalEditOpen(false)}
                            initialData={selectedMessage}
                            onSave={async () => {
                                triggerToast(
                                    "MENSAGEM ATUALIZADA COM SUCESSO!"
                                );
                                await fetchMessages();
                            }}
                            showErrorModal={showErrorModal}
                        />
                    )}
                    {modalDeleteOpen && selectedMessage && (
                        <ModalDeleteMessage
                            open={modalDeleteOpen}
                            close={() => setModalDeleteOpen(false)}
                            data={selectedMessage}
                            onDelete={async () => {
                                triggerToast("MENSAGEM EXCLUÍDA COM SUCESSO!");
                                await fetchMessages();
                            }}
                            showErrorModal={showErrorModal}
                        />
                    )}
                    {showToast && (
                        <Toast
                            message={toastMessage}
                            onClose={() => setShowToast(false)}
                        />
                    )}
                </>
            ) : (
                <ErrorModal
                    open={true}
                    message="Ocorreu um erro."
                    onButtonClick={() => navigate("/refunds")}
                />
            )}
        </>
    );
}
